import React, { useState } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Button,
    Divider,
    Paper,
    IconButton,
    Drawer,
    InputLabel,
} from '@mui/material';
import {
    FilterList,
    Close,
    TuneRounded,
    SortRounded,
    AttachMoneyRounded,
    InventoryRounded,
    CategoryRounded,
    LabelRounded,
    ExpandMoreRounded,
    StorefrontRounded,
} from '@mui/icons-material';
import PriceRangeSlider from './PriceRangeSlider';

const FilterPanel = ({
    // Category & Subcategory props
    categories,
    selectedCategory,
    onCategoryChange,
    subcategories,
    selectedSubcategory,
    onSubcategoryChange,
    // Brand filter props
    brands,
    selectedBrand,
    onBrandChange,
    // Price filter props
    minPrice,
    maxPrice,
    priceRange,
    onPriceChange,
    // Sort props
    sortBy,
    onSortChange,
    // Stock filter props
    // showInStockOnly, // Removed - backend already filters by stock
    // onStockFilterChange,
    // Clear filters
    onClearFilters,
    // Mobile props
    isMobile,
    isOpen,
    onToggle,
}) => {
    const sortOptions = [
        { value: 'default', label: 'Más relevantes' },
        { value: 'price_asc', label: 'Precio: Menor a Mayor' },
        { value: 'price_desc', label: 'Precio: Mayor a Menor' },
        { value: 'name_asc', label: 'A - Z' },
        { value: 'name_desc', label: 'Z - A' },
    ];

    const selectStyles = {
        borderRadius: '8px',
        backgroundColor: '#fff',
        fontSize: '0.95rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
            borderWidth: '1.5px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#801F56',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#801F56',
            borderWidth: '2px',
        },
        '& .MuiSelect-select': {
            padding: '12px 14px',
            fontWeight: 500,
        },
    };

    const labelStyles = {
        fontSize: '0.8rem',
        fontWeight: 700,
        color: '#333',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    };

    const FilterContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                    sx={{
                        fontSize: '1.1rem',
                        fontWeight: 800,
                        color: '#111',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                    }}
                >
                    Filtros
                </Typography>
                {isMobile && (
                    <IconButton onClick={onToggle} size="small">
                        <Close />
                    </IconButton>
                )}
            </Box>

            {/* Category Select */}
            <Box>
                <Typography sx={labelStyles}>
                    <CategoryRounded sx={{ fontSize: 18, color: '#801F56' }} />
                    Categoría
                </Typography>
                <FormControl fullWidth size="small">
                    <Select
                        value={selectedCategory || 'all'}
                        onChange={(e) => onCategoryChange(e.target.value === 'all' ? null : e.target.value)}
                        IconComponent={ExpandMoreRounded}
                        sx={selectStyles}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.95rem',
                                        padding: '10px 16px',
                                        '&:hover': {
                                            backgroundColor: '#FFF5F8',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#F0C9D5',
                                            '&:hover': {
                                                backgroundColor: '#F0C9D5',
                                            },
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="all">
                            <Typography sx={{ fontWeight: 600 }}>Todas las categorías</Typography>
                        </MenuItem>
                        {categories?.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Subcategory Select - only show when category is selected */}
            {selectedCategory && (
                <Box>
                    <Typography sx={labelStyles}>
                        <LabelRounded sx={{ fontSize: 18, color: '#801F56' }} />
                        Subcategoría
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={selectedSubcategory || 'all'}
                            onChange={(e) => onSubcategoryChange(e.target.value === 'all' ? null : e.target.value)}
                            IconComponent={ExpandMoreRounded}
                            sx={selectStyles}
                            disabled={!subcategories || subcategories.length === 0}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        maxHeight: '300px',
                                        '& .MuiMenuItem-root': {
                                            fontSize: '0.95rem',
                                            padding: '10px 16px',
                                            '&:hover': {
                                                backgroundColor: '#FFF5F8',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#F0C9D5',
                                                '&:hover': {
                                                    backgroundColor: '#F0C9D5',
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="all">
                                <Typography sx={{ fontWeight: 600 }}>Todas las subcategorías</Typography>
                            </MenuItem>
                            {subcategories?.map((sub) => (
                                <MenuItem key={sub.idsubcategory} value={sub.idsubcategory}>
                                    {sub.subcategoryName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            <Divider sx={{ borderColor: '#eee' }} />

            {/* Brand Select */}
            <Box>
                <Typography sx={labelStyles}>
                    <StorefrontRounded sx={{ fontSize: 18, color: '#801F56' }} />
                    Marca
                </Typography>
                <FormControl fullWidth size="small">
                    <Select
                        value={selectedBrand || 'all'}
                        onChange={(e) => onBrandChange(e.target.value === 'all' ? null : e.target.value)}
                        IconComponent={ExpandMoreRounded}
                        sx={selectStyles}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    maxHeight: '300px',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.95rem',
                                        padding: '10px 16px',
                                        '&:hover': {
                                            backgroundColor: '#FFF5F8',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#F0C9D5',
                                            '&:hover': {
                                                backgroundColor: '#F0C9D5',
                                            },
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="all">
                            <Typography sx={{ fontWeight: 600 }}>Todas las marcas</Typography>
                        </MenuItem>
                        {brands?.map((brand) => (
                            <MenuItem key={brand.id} value={brand.id}>
                                {brand.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Divider sx={{ borderColor: '#eee' }} />

            {/* Sort Options */}
            <Box>
                <Typography sx={labelStyles}>
                    <SortRounded sx={{ fontSize: 18, color: '#801F56' }} />
                    Ordenar por
                </Typography>
                <FormControl fullWidth size="small">
                    <Select
                        value={sortBy}
                        onChange={(e) => onSortChange(e.target.value)}
                        IconComponent={ExpandMoreRounded}
                        sx={selectStyles}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.95rem',
                                        padding: '10px 16px',
                                        '&:hover': {
                                            backgroundColor: '#FFF5F8',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#F0C9D5',
                                            '&:hover': {
                                                backgroundColor: '#F0C9D5',
                                            },
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Divider sx={{ borderColor: '#eee' }} />

            {/* Price Range Filter */}
            <Box>
                <Typography sx={labelStyles}>
                    <AttachMoneyRounded sx={{ fontSize: 18, color: '#801F56' }} />
                    Rango de Precio
                </Typography>
                <PriceRangeSlider
                    minPrice={minPrice}
                    maxPrice={maxPrice}
                    value={priceRange}
                    onChange={onPriceChange}
                />
            </Box>

            <Divider sx={{ borderColor: '#eee' }} />

            {/* Clear Filters Button */}
            <Button
                fullWidth
                variant="outlined"
                onClick={onClearFilters}
                sx={{
                    borderRadius: '8px',
                    borderWidth: '1.5px',
                    borderColor: '#801F56',
                    color: '#801F56',
                    fontWeight: 700,
                    padding: '10px 20px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderWidth: '1.5px',
                        borderColor: '#801F56',
                        backgroundColor: '#801F56',
                        color: '#fff',
                    },
                }}
            >
                Limpiar filtros
            </Button>
        </Box>
    );

    // Mobile Drawer
    if (isMobile) {
        return (
            <>
                {/* Floating Filter Button for Mobile */}
                <Button
                    variant="contained"
                    onClick={onToggle}
                    startIcon={<TuneRounded />}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 900,
                        borderRadius: '50px',
                        padding: '14px 28px',
                        backgroundColor: '#111',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        '&:hover': {
                            backgroundColor: '#333',
                        },
                    }}
                >
                    Filtros
                </Button>

                {/* Drawer */}
                <Drawer
                    anchor="left"
                    open={isOpen}
                    onClose={onToggle}
                    sx={{
                        zIndex: '998 !important',
                        '& .MuiDrawer-paper': {
                            width: '320px',
                            maxWidth: '85%',
                            padding: 3,
                            backgroundColor: '#fafafa',
                        },
                    }}
                >
                    <FilterContent />
                </Drawer>
            </>
        );
    }

    // Desktop Sidebar
    return (
        <Paper
            elevation={0}
            sx={{
                padding: 3,
                borderRadius: '12px',
                backgroundColor: '#fafafa',
                border: '1px solid #e8e8e8',
                position: 'sticky',
                top: 120,
                maxHeight: 'calc(100vh - 140px)',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#ddd',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#ccc',
                },
            }}
        >
            <FilterContent />
        </Paper>
    );
};

export default FilterPanel;
